import {
  createContext,
  useState,
  useContext,
  useRef,
  useCallback,
  ReactNode,
} from "react";
import * as L from "leaflet";

export type ImportedLayerFormat = "geojson" | "kml" | "shapefile";

export interface ImportedLayer {
  id: string;
  name: string;
  format: ImportedLayerFormat;
  featureCount: number;
  bbox: [number, number, number, number] | null; // [minLng, minLat, maxLng, maxLat]
  // GeoJSON used for geojson/shapefile formats and for rendering
  geojson?: { type: "FeatureCollection"; features: any[]; [key: string]: any };
  // Raw KML document — used by react-leaflet-kml for native rendering
  rawKml?: Document;
  // Whether this KML is safe to render with the native KML plugin
  kmlSafeForPlugin?: boolean;
  // Shapefile extra metadata
  rawPrj?: string;
  rawCpg?: string;
  rawQmd?: string;
  rawShx?: ArrayBuffer;
  isVisible: boolean;
}

interface IImportedLayers {
  importedLayers: ImportedLayer[];
  addImportedLayer: (layer: ImportedLayer) => void;
  removeImportedLayer: (id: string) => void;
  toggleImportedLayerVisibility: (id: string) => void;
  updateImportedLayerName: (id: string, name: string) => void;
  clearImportedLayers: () => void;
  registerLeafletLayer: (id: string, layer: L.Layer) => void;
  unregisterLeafletLayer: (id: string) => void;
  getLeafletLayer: (id: string) => L.Layer | undefined;
}

const ImportedLayersContext = createContext<IImportedLayers>(
  {} as IImportedLayers
);

export const ImportedLayersProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [importedLayers, setImportedLayers] = useState<ImportedLayer[]>([]);
  const layersRef = useRef<Map<string, L.Layer>>(new Map());

  const addImportedLayer = useCallback((layer: ImportedLayer) => {
    setImportedLayers((prev) => [...prev, layer]);
  }, []);

  const removeImportedLayer = useCallback((id: string) => {
    setImportedLayers((prev) => prev.filter((l) => l.id !== id));
  }, []);

  const toggleImportedLayerVisibility = useCallback((id: string) => {
    setImportedLayers((prev) =>
      prev.map((l) => (l.id === id ? { ...l, isVisible: !l.isVisible } : l))
    );
  }, []);

  const updateImportedLayerName = useCallback((id: string, name: string) => {
    setImportedLayers((prev) =>
      prev.map((l) => (l.id === id ? { ...l, name } : l))
    );
  }, []);

  const clearImportedLayers = useCallback(() => {
    setImportedLayers([]);
  }, []);

  const registerLeafletLayer = useCallback(
    (id: string, layer: L.Layer) => {
      layersRef.current.set(id, layer);
    },
    []
  );

  const unregisterLeafletLayer = useCallback((id: string) => {
    layersRef.current.delete(id);
  }, []);

  const getLeafletLayer = useCallback(
    (id: string): L.Layer | undefined => {
      return layersRef.current.get(id);
    },
    []
  );

  return (
    <ImportedLayersContext.Provider
      value={{
        importedLayers,
        addImportedLayer,
        removeImportedLayer,
        toggleImportedLayerVisibility,
        updateImportedLayerName,
        clearImportedLayers,
        registerLeafletLayer,
        unregisterLeafletLayer,
        getLeafletLayer,
      }}
    >
      {children}
    </ImportedLayersContext.Provider>
  );
};

export function useImportedLayers() {
  const context = useContext(ImportedLayersContext);
  if (!context) {
    throw new Error(
      "useImportedLayers must be used within an ImportedLayersProvider"
    );
  }
  return context;
}
